// スキルの系統（4人）：系統ごとに Lv（0..10）をポイントで上げる（1Lv＝1ポイント。振り直しは無料）。
// 系統の Lv を上げるたびにその系統の能力が上がり、Lv2・5・8 で技が開き、Lv10 で系統の極み（開いた技はすべて効く。選ばない）。
// 術はその系統の Lv3 で覚え、Lv7 でその術の奥義。ポイントは主Lvで増える（linePoints）。最後まで全部の系統を Lv10 にはできない量。
// 構成は今までどおり ID の並びで、系統の Lv の印（@lv:<系統>=<Lv>）だけを持つ
import type { Effect, ModKey } from './mods';
import type { CharId } from '../types';
import { CONFIG } from './config';

export interface SkillLine {
  key: string;
  name: string;
  /** 専用ツリーの列（術の系統。術を覚える・術の威力に使う）。無ければ -1 */
  col: number;
  /** 開く技（スキルID）。WAZA_LVS の Lv で順に開き、最後が極み */
  waza: string[];
  /** 系統 Lv 1つあたりの能力（× CONFIG.skills.linePerLv） */
  per: Partial<Record<ModKey, number>>;
}

export const LINE_MAX = 10;
/** 技が開く系統 Lv（最後は極み） */
export const WAZA_LVS = [2, 5, 8, 10] as const;
/** 術を覚える系統 Lv（基本の術は最初から） */
export const LINE_JUTSU_LV = 3;
/** 術の奥義を覚える系統 Lv（妖力が参のとき、持っていく術が奥義になる） */
export const LINE_OUGI_LV = 7;
/** 系統 Lv n までに使うポイント */
export const lineCost = (n: number) => n;
/**
 * ポイント：主Lvの序盤ほど多く増える（3 + 27×√(主Lv/1000)：Lv100で11・Lv300で17・Lv500で22・Lv1000で30）＋上乗せ（式神・道場・外伝。解放力の上乗せ）3つごとに+1（最大+5）。
 * 全部（5系統×10＝50）には届かない：深くする系統を選ぶのがビルド
 */
export const linePoints = (level: number, bonus: number) => Math.floor(3 + 27 * Math.sqrt(Math.max(0, level) / 1000)) + Math.floor(bonus / 3);

/** 博徒（4人とも同じ。賽の目で当たれば大きく、外れると下がる。Lv で速さ・揃い目が上がり、HP は下がる） */
const BAKUTO_LINE = (c: CharId): SkillLine => ({ key: 'bakuto', name: '博徒', col: -1, waza: [`${c}_chouhan`, `${c}_oome`, `${c}_yakusha`, `${c}_kiwami_bakuto`], per: { dicePair: 0.02, agi: 0.006, hp: -0.005 } });

export const SKILL_LINES: Partial<Record<CharId, SkillLine[]>> = {
  suzune: [
    { key: 'fushin', name: '普請', col: 0, waza: ['suzune_zumen', 'suzune_daiku', 'suzune_hayadate', 'suzune_kiwami_fushin'], per: { def: 0.02, buildHp: 0.03, buildPower: 0.02 } },
    { key: 'kojo', name: '攻城', col: 1, waza: ['suzune_kanabo', 'suzune_shirokuzushi', 'suzune_ogui', 'suzune_kiwami_kojo'], per: { atk: 0.04, core: 0.004 } },
    { key: 'mamori', name: '守り', col: 2, waza: ['suzune_fudo', 'suzune_dandori', 'suzune_toryo', 'suzune_kiwami_mamori'], per: { hp: 0.04, def: 0.03 } },
    { key: 'kakusei', name: '覚醒', col: 3, waza: ['suzune_hayazame', 'suzune_tegiwa', 'suzune_ofushin', 'suzune_kishin'], per: { hp: 0.02, atk: 0.02, agi: 0.004 } },
    BAKUTO_LINE('suzune'),
  ],
  akari: [
    { key: 'hanabi', name: '花火', col: 0, waza: ['akari_kakusan', 'akari_bigbloom', 'akari_hyakka', 'akari_kiwami_hanabi'], per: { skillPower: 0.02, splash: 0.012 } },
    { key: 'jintori', name: '陣取り', col: 1, waza: ['akari_lantern', 'akari_gyoretsu', 'akari_senka', 'akari_kiwami_jintori'], per: { atk: 0.03, territory: 0.002 } },
    { key: 'mamori', name: '守り', col: 2, waza: ['akari_kekkai', 'akari_mamori', 'akari_joyato', 'akari_kiwami_mamori'], per: { hp: 0.04, def: 0.03 } },
    { key: 'kakusei', name: '覚醒', col: 3, waza: ['akari_hayazame', 'akari_hanabi', 'akari_dairin', 'akari_hoshifuri'], per: { hp: 0.02, atk: 0.02, agi: 0.004 } },
    BAKUTO_LINE('akari'),
  ],
  kazamaru: [
    { key: 'sokko', name: '速攻', col: 0, waza: ['kazamaru_hayagake', 'kazamaru_ikioi', 'kazamaru_shinsoku', 'kazamaru_kiwami_sokko'], per: { atk: 0.04 } },
    { key: 'ridatsu', name: '離脱', col: 1, waza: ['kazamaru_shisso', 'kazamaru_hanare', 'kazamaru_kakure', 'kazamaru_kiwami_ridatsu'], per: { agi: 0.01, def: 0.02, hp: 0.015 } },
    { key: 'kari', name: '狩り', col: 2, waza: ['kazamaru_senpu', 'kazamaru_kamaitachi', 'kazamaru_oroshi', 'kazamaru_kiwami_kari'], per: { skillPower: 0.03, neutral: 0.01 } },
    { key: 'kakusei', name: '覚醒', col: 3, waza: ['kazamaru_hayazame', 'kazamaru_oikaze', 'kazamaru_tengukaze', 'kazamaru_daitengu'], per: { hp: 0.02, atk: 0.02, agi: 0.004 } },
    BAKUTO_LINE('kazamaru'),
  ],
  yuzuha: [
    { key: 'geigeki', name: '迎撃', col: 0, waza: ['yuzuha_iai', 'yuzuha_kaeshigatana', 'yuzuha_mamori', 'yuzuha_kiwami_geigeki'], per: { def: 0.03, hp: 0.02 } },
    { key: 'kitsunebi', name: '狐火', col: 1, waza: ['yuzuha_kekkai', 'yuzuha_onibi', 'yuzuha_yomeiri', 'yuzuha_kiwami_kitsunebi'], per: { skillPower: 0.03, neutral: 0.01 } },
    { key: 'kettou', name: '決闘', col: 2, waza: ['yuzuha_kyubi', 'yuzuha_ikkiuchi', 'yuzuha_hissatsu', 'yuzuha_kiwami_kettou'], per: { atk: 0.035, hp: 0.015 } },
    { key: 'kakusei', name: '覚醒', col: 3, waza: ['yuzuha_hayazame', 'yuzuha_kawaki', 'yuzuha_issen', 'yuzuha_kyubinokitsune'], per: { hp: 0.02, atk: 0.02, agi: 0.004 } },
    BAKUTO_LINE('yuzuha'),
  ],
};

export const skillLines = (c: CharId | null | undefined): SkillLine[] | undefined => (c ? SKILL_LINES[c] : undefined);
export const lineToken = (i: number, lv: number) => `@lv:${i}=${lv}`;
const TOKEN = /^@lv:(\d+)=(\d+)$/;
export const isLineToken = (id: string) => TOKEN.test(id);
/** 印から系統ごとの Lv（無い系統は0） */
export function lineLevels(c: CharId, ids: readonly string[]): number[] {
  const L = SKILL_LINES[c] ?? [];
  const out = L.map(() => 0);
  for (const id of ids) {
    const m = TOKEN.exec(id);
    if (m && Number(m[1]) < out.length) out[Number(m[1])] = Math.max(0, Math.min(LINE_MAX, Number(m[2])));
  }
  return out;
}
/** 技の系統（そのキャラの系統で、何番目か）と開く Lv */
export function lineOfWaza(c: CharId, id: string): { line: number; need: number; kiwami: boolean } | undefined {
  const L = SKILL_LINES[c] ?? [];
  for (let i = 0; i < L.length; i++) {
    const k = L[i].waza.indexOf(id);
    if (k >= 0) return { line: i, need: WAZA_LVS[k], kiwami: k === WAZA_LVS.length - 1 };
  }
  return undefined;
}
/** 系統 Lv で開いている技（極みを含む） */
export const lineOpenWaza = (line: SkillLine, lv: number) => line.waza.filter((_, k) => lv >= WAZA_LVS[k]);
/** 系統 Lv の能力（Lv×1つあたり） */
export function lineEffects(line: SkillLine, lv: number): Effect[] {
  const spent = lv * CONFIG.skills.linePerLv;
  return Object.entries(line.per)
    .map(([k, v]) => ({ key: k as ModKey, value: Math.round((v as number) * spent * 1000) / 1000 }))
    .filter((e) => e.value !== 0);
}
